// @workflow assistant.history
/**
 * Conversation history from the server (aiva.api B1: GET api/assistant/conversations and
 * GET …/{id}/events). The list is grouped by last activity; a stored conversation is replayed by
 * folding its events with the same reducer the live stream uses, plus the clean-ups the live UI
 * did at the time.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { EVENT, ROLE, MESSAGE_STATUS, createConversation, applyEvent } from './events.js';

export const HISTORY_GROUP = Object.freeze({ TODAY: 'Today', YESTERDAY: 'Yesterday', WEEK: 'Last 7 days', EARLIER: 'Earlier' });
const UNTITLED = 'Untitled conversation';
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_DAYS = 7;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function groupFor(date, now) {
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days <= 0) return HISTORY_GROUP.TODAY;
  if (days === 1) return HISTORY_GROUP.YESTERDAY;
  if (days < WEEK_DAYS) return HISTORY_GROUP.WEEK;
  return HISTORY_GROUP.EARLIER;
}

/**
 * @param {Array<{ id: string, title?: string, updatedAt: string }>} items the server list
 * @param {Date} now
 * @returns {Array<{ group: string, items: Array<{ id: string, title: string, note: string }> }>}
 */
export function groupConversations(items, now) {
  const rows = (items || [])
    .filter((i) => i && i.id && !Number.isNaN(new Date(i.updatedAt).getTime()))
    .map((i) => ({ ...i, at: new Date(i.updatedAt) }))
    .sort((a, b) => b.at - a.at);
  const groups = [];
  for (const row of rows) {
    const group = groupFor(row.at, now);
    const note = group === HISTORY_GROUP.TODAY
      ? row.at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : row.at.toLocaleDateString([], { day: 'numeric', month: 'short' });
    const item = { id: row.id, title: (row.title || '').trim() || UNTITLED, note };
    const last = groups[groups.length - 1];
    if (last && last.group === group) last.items.push(item);
    else groups.push({ group, items: [item] });
  }
  return groups;
}

/** A failed reply the user retried: live, RETRY removed it and asked again, so drop it and the first ask. */
function withoutRetriedFailures(conversation) {
  const { messages } = conversation;
  const drop = new Set();
  messages.forEach((m, i) => {
    if (m.role !== ROLE.ASSISTANT || m.status !== MESSAGE_STATUS.FAILED) return;
    const asked = messages[i - 1];
    const askedAgain = messages.slice(i + 1).find((x) => x.role === ROLE.USER);
    if (asked && asked.role === ROLE.USER && askedAgain && askedAgain.text === asked.text) {
      drop.add(asked.id);
      drop.add(m.id);
    }
  });
  return drop.size ? { ...conversation, messages: messages.filter((m) => !drop.has(m.id)) } : conversation;
}

/** A reply still streaming or awaiting in the log was cut off (the page closed): show it stopped. */
function withEndedReplies(conversation) {
  return conversation.messages
    .filter((m) => m.role === ROLE.ASSISTANT && (m.status === MESSAGE_STATUS.STREAMING || m.status === MESSAGE_STATUS.AWAITING))
    .reduce((c, m) => applyEvent(c, { type: EVENT.MESSAGE_END, messageId: m.id, stopped: true }), conversation);
}

/**
 * @param {string} conversationId the UI's id (the server keeps it as ClientConversationId)
 * @param {Array<object>} events stored events in Seq order
 */
export function replayConversation(conversationId, events) {
  const folded = (events || []).reduce(applyEvent, createConversation(conversationId));
  return withEndedReplies(withoutRetriedFailures(folded));
}

/** The list without the given conversations (deleted, or waiting for their Undo window to end). */
export function withoutConversations(groups, ids) {
  if (!ids || !ids.length) return groups;
  const hidden = new Set(ids);
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !hidden.has(i.id)) }))
    .filter((g) => g.items.length);
}
